import {
  AccountBookOutlined,
  BarcodeOutlined,
  ApiOutlined,
  AuditOutlined,
  FileProtectOutlined,
  BankOutlined,
  CarOutlined,
  BarChartOutlined,
  CalculatorOutlined,
  CloudOutlined,
  CreditCardOutlined,
  DatabaseOutlined,
  ExportOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  FundOutlined,
  GiftOutlined,
  HistoryOutlined,
  HomeOutlined,
  ImportOutlined,
  LockOutlined,
  PercentageOutlined,
  InboxOutlined,
  LineChartOutlined,
  RollbackOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  SwapOutlined,
  TagsOutlined,
  TeamOutlined,
  UndoOutlined,
  StopOutlined,
} from "@ant-design/icons";
import type { ReactNode } from "react";
import type { Permission } from "@/api/client";
import type { MessageKey } from "@/i18n";

export type NavItem = {
  /** a page's route; "sec:…" for a section heading, "grp:…" for a group */
  key: string;
  label: MessageKey;
  icon?: ReactNode;
  perms: Permission[];
  children?: NavItem[];
  /** a heading over its entries (Penjualan · OMS), not a page */
  section?: boolean;
  /** detail routes outside the page's own path that belong to it */
  also?: string[];
};

export const HOME = "/app";

const WMS: Permission[] = ["warehouse.operate"];
const FIN: Permission[] = ["finance.view"];
const PROC: Permission[] = ["procurement.view"];

// Menu structure and access follow App Flow §2.3 and §10, grouped as an ERP:
// sections (sales, warehouse, purchasing, finance, system) hold pages or
// groups of pages, never deeper. The sidebar and the tab bar both read it, so
// a page's tab title is always its menu label.
export const NAV: NavItem[] = [
  { key: HOME, label: "nav.dashboard", icon: <HomeOutlined />, perms: ["dashboard.view"] },
  { key: "/app/analytics", label: "nav.analytics", icon: <BarChartOutlined />, perms: ["orders.view"] },
  {
    key: "sec:sales",
    label: "nav.section.sales",
    section: true,
    perms: ["orders.view", "catalog.view", "inventory.view", "channels.view"],
    children: [
      { key: "/app/orders", label: "nav.orders", icon: <ShoppingCartOutlined />, perms: ["orders.view"] },
      { key: "/app/aftersales", label: "nav.aftersales", icon: <RollbackOutlined />, perms: ["orders.view"] },
      { key: "/app/aftersales/claims", label: "nav.aftersalesClaims", icon: <FileProtectOutlined />, perms: ["orders.view"] },
      { key: "/app/promotions", label: "nav.promotions", icon: <GiftOutlined />, perms: ["orders.view"] },
      { key: "/app/couriers", label: "nav.couriers", icon: <CarOutlined />, perms: ["orders.view"] },
      {
        key: "grp:products",
        label: "nav.group.products",
        icon: <TagsOutlined />,
        perms: ["catalog.view"],
        // Product (the template), each store's listings, the stock unit, the price.
        children: [
          { key: "/app/products", label: "nav.products", perms: ["catalog.view"] },
          { key: "/app/listings", label: "nav.listings", perms: ["catalog.view"], also: ["/app/catalog/duplicates"] },
          { key: "/app/catalog", label: "nav.catalog", perms: ["catalog.view"] },
          { key: "/app/prices", label: "nav.prices", perms: ["catalog.view"] },
        ],
      },
      {
        key: "grp:stock",
        label: "nav.group.stock",
        icon: <InboxOutlined />,
        perms: ["inventory.view"],
        children: [
          { key: "/app/inventory", label: "nav.inventory", perms: ["inventory.view"] },
          { key: "/app/stock-sync", label: "nav.stockSync", perms: ["inventory.view"] },
        ],
      },
      { key: "/app/channels", label: "nav.channels", icon: <ApiOutlined />, perms: ["channels.view"] },
    ],
  },
  {
    key: "sec:wms",
    label: "nav.section.warehouse",
    section: true,
    perms: ["warehouse.operate", "inventory.view"],
    children: [
      {
        key: "grp:inbound",
        label: "nav.group.inbound",
        icon: <ImportOutlined />,
        perms: WMS,
        children: [
          { key: "/app/warehouse/inbound", label: "nav.warehouse.inbound", perms: WMS },
          { key: "/app/warehouse/putaway", label: "nav.warehouse.putaway", perms: WMS },
        ],
      },
      {
        key: "grp:outbound",
        label: "nav.group.outbound",
        icon: <ExportOutlined />,
        perms: WMS,
        children: [
          { key: "/app/warehouse/pools", label: "nav.warehouse.pools", perms: WMS },
          { key: "/app/warehouse/waves", label: "nav.warehouse.waves", perms: WMS },
          // A task's detail opens from picking or putaway; it names its own tab.
          { key: "/app/warehouse/picking", label: "nav.warehouse.picking", perms: WMS, also: ["/app/warehouse/tasks"] },
          { key: "/app/warehouse/packing", label: "nav.warehouse.packing", perms: WMS },
          { key: "/app/warehouse/outbound", label: "nav.warehouse.outbound", perms: WMS },
        ],
      },
      { key: "/app/warehouse/returns", label: "nav.warehouse.returns", icon: <UndoOutlined />, perms: WMS },
      { key: "/app/warehouse/cancelled", label: "nav.warehouse.cancelled", icon: <StopOutlined />, perms: WMS },
      { key: "/app/warehouse/performance", label: "nav.warehouse.performance", icon: <LineChartOutlined />, perms: WMS },
      {
        key: "grp:whstock",
        label: "nav.group.whStock",
        icon: <DatabaseOutlined />,
        perms: ["warehouse.operate", "inventory.view"],
        children: [
          { key: "/app/warehouse/stock", label: "nav.warehouse.stock", perms: ["warehouse.operate", "inventory.view"] },
          { key: "/app/warehouse/locations", label: "nav.warehouse.locations", perms: WMS },
          { key: "/app/warehouse/containers", label: "nav.warehouse.containers", perms: WMS },
          { key: "/app/warehouse/pallets", label: "nav.warehouse.pallets", perms: WMS },
          { key: "/app/warehouse/transfers", label: "nav.warehouse.transfers", perms: WMS },
          { key: "/app/warehouse/counts", label: "nav.warehouse.counts", perms: WMS },
          { key: "/app/warehouse/variance", label: "nav.warehouse.variance", perms: ["inventory.view", "warehouse.manage"] },
          { key: "/app/warehouse/serials", label: "nav.warehouse.serials", perms: ["warehouse.operate", "inventory.view"] },
          { key: "/app/warehouse/damaged", label: "nav.warehouse.damaged", perms: ["warehouse.operate", "inventory.view"] },
        ],
      },
    ],
  },
  {
    key: "sec:purchasing",
    label: "nav.section.purchasing",
    section: true,
    perms: PROC,
    children: [
      { key: "/app/procurement/orders", label: "nav.procurement.orders", icon: <FileTextOutlined />, perms: PROC },
      { key: "/app/procurement/returns", label: "nav.procurement.returns", icon: <SwapOutlined />, perms: PROC },
      { key: "/app/procurement/suppliers", label: "nav.procurement.suppliers", icon: <TeamOutlined />, perms: PROC },
    ],
  },
  {
    key: "sec:finance",
    label: "nav.section.finance",
    section: true,
    perms: FIN,
    children: [
      { key: "/app/finance/reports", label: "nav.finance.reports", icon: <FundOutlined />, perms: FIN },
      { key: "/app/finance/cash", label: "nav.finance.cash", icon: <BankOutlined />, perms: FIN },
      { key: "/app/finance/receivables", label: "nav.finance.receivables", icon: <AccountBookOutlined />, perms: FIN },
      { key: "/app/finance/bills", label: "nav.finance.bills", icon: <AuditOutlined />, perms: FIN },
      { key: "/app/finance/credits", label: "nav.finance.credits", icon: <FileDoneOutlined />, perms: FIN },
      { key: "/app/finance/costs", label: "nav.finance.costs", icon: <CalculatorOutlined />, perms: FIN },
      { key: "/app/finance/margins", label: "nav.finance.margins", icon: <PercentageOutlined />, perms: FIN },
      { key: "/app/finance/books", label: "nav.finance.books", icon: <LockOutlined />, perms: FIN },
    ],
  },
  {
    key: "sec:tools",
    label: "nav.section.tools",
    section: true,
    perms: ["warehouse.operate", "catalog.view"],
    children: [{ key: "/app/tools/barcodes", label: "nav.tools.barcodes", icon: <BarcodeOutlined />, perms: ["warehouse.operate", "catalog.view"] }],
  },
  {
    key: "sec:system",
    label: "nav.section.system",
    section: true,
    perms: ["logs.all", "logs.warehouse", "logs.orders", "settings.company", "settings.users", "settings.warehouses", "billing.manage"],
    children: [
      { key: "/app/logs", label: "nav.logs", icon: <HistoryOutlined />, perms: ["logs.all", "logs.warehouse", "logs.orders"] },
      {
        key: "grp:settings",
        label: "nav.settings",
        icon: <SettingOutlined />,
        perms: ["settings.company", "settings.users", "settings.warehouses"],
        children: [
          { key: "/app/settings/company", label: "nav.settings.company", perms: ["settings.company"] },
          { key: "/app/settings/warehouses", label: "nav.settings.warehouses", perms: ["settings.warehouses"] },
          { key: "/app/settings/labels", label: "nav.settings.labels", perms: ["settings.warehouses"] },
          { key: "/app/settings/users", label: "nav.settings.users", perms: ["settings.users"] },
        ],
      },
      { key: "/app/billing", label: "nav.billing", icon: <CreditCardOutlined />, perms: ["billing.manage"] },
      { key: "/app/storage", label: "nav.storage", icon: <CloudOutlined />, perms: ["billing.manage", "settings.warehouses"] },
    ],
  },
];

/** A page entry with the section and group above it. */
export type NavPage = { item: NavItem; trail: NavItem[] };

function collect(list: NavItem[], trail: NavItem[], out: NavPage[]) {
  for (const n of list) {
    if (n.children) collect(n.children, [...trail, n], out);
    else out.push({ item: n, trail });
  }
  return out;
}

/** Every page of the menu, in menu order. */
export const PAGES: NavPage[] = collect(NAV, [], []);

export function isPage(key: string): boolean {
  return key.startsWith("/");
}

/**
 * The deepest menu entry that is the path or a prefix of it. Home matches only
 * itself: every app path starts with /app, so as a prefix it would match all.
 */
export function navFor(pathname: string): NavItem | undefined {
  const under = (prefix: string) => pathname === prefix || (prefix !== HOME && pathname.startsWith(prefix + "/"));
  let best: { item: NavItem; len: number } | undefined;
  for (const { item } of PAGES) {
    for (const prefix of [item.key, ...(item.also ?? [])]) {
      if (under(prefix) && (!best || prefix.length > best.len)) best = { item, len: prefix.length };
    }
  }
  return best?.item;
}

/** The section and group keys above a page, to open them in the menu. */
export function trailOf(key: string): string[] {
  return PAGES.find((p) => p.item.key === key)?.trail.map((n) => n.key) ?? [];
}
